import React, { useState } from 'react';
import { Button } from 'src/components/common';
import { CompanyAPIService } from 'src/services/company.service';
import { TStartupPaymentRequest, TStartupPaymentResponse } from 'src/services/apiEndpoint.types.ts';
import Header from 'src/components/demo-day/header/Header';
import { openPopup } from 'src/store/common/common.slice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetIdentity } from 'src/hooks';
import styles from './paymentCard.module.scss';
import { AppDispatch } from 'src/store/store';
import { htractionWhite, InvestorList } from 'src/assets/images';

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    email: string;
  };
}

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => {
      open: () => void;
    };
  }
}

const loadRazorpayScript = () => {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.body.appendChild(script);
  });
};

const PaymentCard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { getIdentities } = useGetIdentity();
  const { authenticatedId, authenticatedEmail } = getIdentities();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [amountInput, setAmountInput] = useState('1000');
  const [amountError, setAmountError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isValidCoupon, setIsValidCoupon] = useState(false);
  const [couponMsg, setCouponMsg] = useState('');

  const quickAmounts = [1000, 2000, 3000, 5000];

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAmountInput(value);
    setIsValidCoupon(false);
    setDiscount(0);
    setCouponMsg('');
    setCouponCode('');
    if (!value) {
      setAmount(0);
      setAmountError('Amount is required');
      return;
    }
    const num = parseInt(value, 10);
    if (num % 1000 !== 0) {
      setAmount(num);
      setAmountError('Amount must be a multiple of 1000');
    } else {
      setAmount(num);
      setAmountError('');
    }
  };

  const handleQuickAmount = (val: number) => {
    setAmount(val);
    setAmountInput(val.toString());
    setAmountError('');
    setIsValidCoupon(false);
    setDiscount(0);
    setCouponMsg('');
    setCouponCode('');
  };

  const validateCoupon = (code: string) => {
    if (!amount || amount < 1000 || amount % 1000 !== 0) {
      setCouponMsg('Enter a valid amount first');
      setIsValidCoupon(false);
      setDiscount(0);
      return;
    }
    if (code === 'HTRACTION100') {
      setIsValidCoupon(true);
      setDiscount(Math.min(1000, amount));
      setCouponMsg('₹1000 discount applied!');
      dispatch(openPopup({
        popupMessage: 'Coupon applied successfully! ₹1000 discount applied.',
        popupType: 'success'
      }));
    } else if (code === 'HTRACTION' || code === 'WEWORKLABS') {
      setIsValidCoupon(true);
      setDiscount(amount * 0.5);
      setCouponMsg('50% discount applied!');
      dispatch(openPopup({
        popupMessage: 'Coupon applied successfully! 50% discount applied.',
        popupType: 'success'
      }));
    } else {
      setIsValidCoupon(false);
      setDiscount(0);
      setCouponMsg('Invalid coupon code');
      dispatch(openPopup({
        popupMessage: 'Invalid coupon code',
        popupType: 'error'
      }));
    }
  };

  const handlePayment = async () => {
    const finalAmount = Math.max(amount - discount, 0);
    if (!authenticatedId) {
      dispatch(openPopup({
        popupMessage: 'User not authenticated',
        popupType: 'error'
      }));
      return;
    }
    if (amountError || !amount || amount < 1000 || amount % 1000 !== 0) {
      dispatch(openPopup({
        popupMessage: 'Please enter a valid amount (multiple of 1000)',
        popupType: 'error'
      }));
      return;
    }
    // If coupon is HTRACTION100 and finalAmount is 0, just mark as paid
    if (couponCode === 'HTRACTION100' && isValidCoupon && finalAmount === 0) {
      setLoading(true);
      try {
        const companyService = new CompanyAPIService();
        await companyService.startupPayment({
          companyId: authenticatedEmail,
          amount: 0,
          couponCode: 'HTRACTION100'
        });
        dispatch(openPopup({
          popupMessage: 'Coupon applied successfully',
          popupType: 'success'
        }));
        // navigate('/score');
        window.location.href = '/score';
      } catch (error) {
        dispatch(openPopup({
          popupMessage: 'Failed to apply coupon',
          popupType: 'error'
        }));
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);
      const companyService = new CompanyAPIService();
      const paymentRequest: TStartupPaymentRequest = {
        companyId: authenticatedEmail,
        amount: finalAmount,
        couponCode: isValidCoupon ? couponCode : undefined
      };
      const orderResponse = await companyService.startupPayment(paymentRequest);
      try {
        await loadRazorpayScript();
      } catch (error) {
        throw new Error('Failed to load payment gateway. Please try again.');
      }
      if (!orderResponse.status || !orderResponse.data) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }
      const orderData = orderResponse.data as TStartupPaymentResponse;
      const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_ZUAz1Tft8aK0yB';
      const options: RazorpayOptions = {
        key: razorpayKeyId,
        amount: orderData.amount,
        currency: 'INR',
        name: 'htraction',
        description: 'Startup Registration - Limited Time Offer',
        order_id: orderData.orderId,
        prefill: {
          email: authenticatedEmail
        },
        handler: async (response: RazorpayResponse) => {
          try {
            dispatch(openPopup({
              popupMessage: 'Payment successful!',
              popupType: 'success'
            }));
            // navigate('/score');
            window.location.href = '/score';
          } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Payment verification failed';
            dispatch(openPopup({
              popupMessage: errorMessage,
              popupType: 'error'
            }));
          }
        }
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Payment initialization failed';
      dispatch(openPopup({
        popupMessage: errorMessage,
        popupType: 'error'
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <Header />
      <div className={styles.pageContainer}>
        <div className={styles.paymentCard}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Your Path to Investors</h2>
            <div style={{ color: '#4a5568', fontWeight: 400, fontSize: 13, marginBottom: 24 }}>
              Making <span style={{ color: '#FF4B26', fontWeight: 500 }}>Investor connections</span> happen
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, justifyContent: 'center', width: '100%' }}>
              <div style={{
                flex: 1,
                maxWidth: 220,
                minWidth: 180,
                background: 'linear-gradient(135deg, #4f9cff 0%, #3b82f6 100%)',
                borderRadius: 12,
                padding: '12px 16px',
                color: '#fff',
                fontWeight: 500,
                fontSize: 15,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: 80,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="credits">🪙</span> How Credits Work ?
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontWeight: 400, fontSize: 9, textAlign: 'left', color: '#e0edff' }}>
                  <li>1,000 credits = ₹1,000</li>
                  <li>1 Investor application = 1,000 credits</li>
                </ul>
              </div>
              <div style={{
                flex: 1,
                maxWidth: 220,
                minWidth: 180,
                background: 'linear-gradient(135deg, #ff7e5a 0%, #ff4b26 100%)',
                borderRadius: 12,
                padding: '12px 16px',
                color: '#fff',
                fontWeight: 500,
                fontSize: 15,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: 80,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="benefits">✨</span> Benefits
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontWeight: 400, fontSize: 9, textAlign: 'left', color: '#ffe3d6' }}>
                  <li>Accelerate your <span style={{ fontWeight: 600, color: '#fff' }}>fundraising</span></li>
                  <li>Apply to <span style={{ fontWeight: 600, color: '#fff' }}>matched investors</span></li>
                </ul>
              </div>
            </div>
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span role="img" aria-label="buy">🪙</span> Buy Credits
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <input
              type="number"
              min={1000}
              step={1000}
              value={amountInput}
              onChange={handleAmountChange}
              placeholder="Enter the amount of credits"
              style={{
                border: 'none',
                borderBottom: '2px solid #e2e8f0',
                outline: 'none',
                fontSize: 13,
                fontWeight: 600,
                width: 180,
                background: 'transparent',
                color: '#1a1a1a',
                padding: '2px 0',
                textAlign: 'left'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {quickAmounts.map((val) => (
              <button
                key={val}
                type="button"
                style={{
                  padding: '8px 18px',
                  borderRadius: 24,
                  border: '1px solid #e2e8f0',
                  background: amount === val ? 'linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%)' : '#fff',
                  color: amount === val ? '#fff' : '#1a1a1a',
                  fontWeight: 600,
                  fontSize: 10,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: 80
                }}
                onClick={() => handleQuickAmount(val)}
              >
                +{val.toLocaleString('en-IN')}
              </button>
            ))}
          </div>
          {amountError && <div style={{ color: '#ff4b26', fontSize: 13, marginTop: 2 }}>{amountError}</div>}
          <div style={{ borderTop: '1px solid #e2e8f0', margin: '18px 0 10px' }}></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
            <span>Total Amount</span>
            <span>₹{amount || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#059669', marginBottom: 8 }}>
            <span>Discount Amount</span>
            <span>-₹{discount || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, marginBottom: 18 }}>
            <span></span>
            <span>₹{Math.max(amount - discount, 0)}</span>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 18, border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, color: '#222' }}>Apply Coupon</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <input
                  type="text"
                  placeholder="Enter Coupon Code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  style={{
                    border: 'none',
                    borderBottom: '2px solid #e2e8f0',
                    outline: 'none',
                    fontSize: 15,
                    fontWeight: 400,
                    background: 'transparent',
                    color: '#222',
                    padding: '6px 0 4px 0',
                    marginBottom: 0,
                    textTransform: 'uppercase'
                  }}
                />
              </div>
              <button
                onClick={() => validateCoupon(couponCode)}
                disabled={!amount || !!amountError}
                style={{
                  background: 'linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%)',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: 15,
                  borderRadius: 8,
                  padding: '8px 22px',
                  border: 'none',
                  marginLeft: 16,
                  marginBottom: 2,
                  cursor: !amount || !!amountError ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  boxShadow: '0 2px 8px rgba(255, 75, 38, 0.10)'
                }}
              >
                Apply
              </button>
            </div>
            {couponMsg && (
              <div style={{ color: isValidCoupon ? '#059669' : '#ff4b26', fontSize: 13, marginTop: 4 }}>{couponMsg}</div>
            )}
          </div>
          <button
            onClick={handlePayment}
            className={styles.payButton}
            disabled={loading || !!amountError || !amount || amount < 1000}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              background: 'linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%)',
              minHeight: 20,
              maxHeight: 40,
              fontSize: 14,
              fontWeight: 700,
              marginTop: 0,
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(255, 75, 38, 0.10)',
              width: '100%'
            }}
          >
            <span style={{ opacity: loading ? 0 : 1, textAlign: 'center', width: '100%', display: 'block' }}>
              {Math.max(amount - discount, 0) === 0 ? 'Pay Now' : `Pay Now`}
            </span>
            {loading && (
              <>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2
                  }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '2.5px solid rgba(255,255,255,0.4)',
                      borderTop: '2.5px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      background: 'transparent'
                    }}
                  />
                </div>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;
